import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Grid2,
  Paper,
  Chip,
} from "@mui/material";
import {
  PeopleOutlined,
  LocationOnOutlined,
  MenuBookOutlined,
  BarChartOutlined,
  SettingsOutlined,
  SchoolOutlined,
  PersonOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setLocation } from "../Reducers/navigation";

// ─── Metric card ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, trend }) {
  return (
    <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.75 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="h5" fontWeight={500} lineHeight={1}>
          {value}
        </Typography>
        {trend && (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <TrendingUpOutlined sx={{ fontSize: 13, color: "success.main" }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>
              {trend}
            </Typography>
          </Stack>
        )}
      </Stack>
      {sub && (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ─── Action card ─────────────────────────────────────────────────────────────

function ActionCard({ icon: Icon, iconBg, iconColor, title, description, badge, featured, tag, onClick }) {
  return (
    <Paper
      onClick={onClick}
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 2.25,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        height: "100%",
        border: featured ? "2px solid" : "0.5px solid",
        borderColor: featured ? "primary.main" : "divider",
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": {
          bgcolor: "action.hover",
          borderColor: featured ? "primary.dark" : "text.disabled",
        },
        "&:active": { transform: "scale(0.99)" },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20, color: iconColor }} />
        </Box>
        {tag && (
          <Chip
            label={tag}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, height: 20, borderRadius: 1 }}
          />
        )}
      </Stack>

      <Box>
        <Typography variant="body2" fontWeight={500}>
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ lineHeight: 1.5, mt: 0.25 }}
        >
          {description}
        </Typography>
      </Box>

      {badge && (
        <Box
          sx={{
            display: "inline-flex",
            alignSelf: "flex-start",
            px: 1,
            py: 0.25,
            borderRadius: 10,
            bgcolor: "primary.50",
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: 10, fontWeight: 600, color: "primary.800" }}
          >
            {badge}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeading({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 500,
        color: "text.disabled",
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

// ─── Action groups ────────────────────────────────────────────────────────────

const PRIMARY_ACTIONS = [
  {
    key: "Analytics",
    title: "Analytics",
    description: "Overview of session activity, student progress, and program performance across locations.",
    icon: BarChartOutlined,
    iconBg: "#E6F1FB",
    iconColor: "#185FA5",
    featured: true,
    badge: "Start here",
  },
  {
    key: "StudentData",
    title: "Student data",
    description: "Search, filter, and review student records, session history, and assessment results.",
    icon: SchoolOutlined,
    iconBg: "#EEEDFE",
    iconColor: "#534AB7",
    featured: true,
  },
  {
    key: "TutorData",
    title: "Tutor data",
    description: "Review tutor shift records, session logs, and performance across programs.",
    icon: PersonOutlined,
    iconBg: "#E1F5EE",
    iconColor: "#0F6E56",
    featured: true,
  },
];

const MANAGEMENT_ACTIONS = [
  {
    key: "User",
    title: "User management",
    description: "Add, edit, or remove staff accounts and manage role permissions.",
    icon: PeopleOutlined,
    iconBg: "#FAECE7",
    iconColor: "#993C1D",
    tag: "Admin",
  },
  {
    key: "Location",
    title: "Location management",
    description: "Manage site locations, assign programs, semesters, and subjects.",
    icon: LocationOnOutlined,
    iconBg: "#FAEEDA",
    iconColor: "#854F0B",
    tag: "Admin",
  },
  {
    key: "Curriculum",
    title: "Curriculum",
    description: "Configure assessments, materials, and subject content across programs.",
    icon: MenuBookOutlined,
    iconBg: "#E0F7FA",
    iconColor: "#00838F",
    tag: "Admin",
  },
  {
    key: "Settings",
    title: "Settings",
    description: "Manage account preferences, notifications, and organization settings.",
    icon: SettingsOutlined,
    iconBg: "#F0F0F0",
    iconColor: "#6B7280",
  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminLandingPage() {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.role);

  const firstName = role?.first_name ?? "there";
  const orgName = role?.organization_name ?? null;

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  function navigate(key) {
    dispatch(setLocation(key));
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>

      {/* ── Greeting ───────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={500}>
            {greeting}, {firstName}
          </Typography>
          {orgName && (
            <Chip
              label={orgName}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {today}
        </Typography>
      </Box>

      {/* ── Metrics ────────────────────────────────────────────── */}
      <Grid2 container spacing={1.25} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Locations" value="—" sub="Active sites" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Active students" value="—" sub="This semester" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Sessions logged" value="—" sub="This month" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Staff members" value="—" sub="Across locations" />
        </Grid2>
      </Grid2>

      {/* ── Primary actions ─────────────────────────────────────── */}
      <SectionHeading>Overview & data</SectionHeading>
      <Grid2 container spacing={1.5} sx={{ mb: 3 }}>
        {PRIMARY_ACTIONS.map((action) => (
          <Grid2 key={action.key} size={{ xs: 12, sm: 6, md: 4 }}>
            <ActionCard
              icon={action.icon}
              iconBg={action.iconBg}
              iconColor={action.iconColor}
              title={action.title}
              description={action.description}
              badge={action.badge}
              featured={action.featured}
              onClick={() => navigate(action.key)}
            />
          </Grid2>
        ))}
      </Grid2>

      {/* ── Management actions ──────────────────────────────────── */}
      <SectionHeading>Management</SectionHeading>
      <Grid2 container spacing={1.5}>
        {MANAGEMENT_ACTIONS.map((action) => (
          <Grid2 key={action.key} size={{ xs: 12, sm: 6, md: 3 }}>
            <ActionCard
              icon={action.icon}
              iconBg={action.iconBg}
              iconColor={action.iconColor}
              title={action.title}
              description={action.description}
              tag={action.tag}
              onClick={() => navigate(action.key)}
            />
          </Grid2>
        ))}
      </Grid2>

    </Box>
  );
}